import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { get } from 'lodash/fp';
import { getFormValues } from 'redux-form';
import { createSelector } from 'reselect';
import {
    GeneralEnterprise as Enterprise,
    GeneralPricingMode as PricingMode,
    ProductPricing,
} from '@mews-commander/data-access';
import { getProp, getStringProp } from '@mews/fp';
import { ApplicationState, Taxation, WithServiceId } from '@mews-commander/types';
import { AppClockService, DateService, DateTimeService, NumberService, TimeService } from '@mews/framework';
import {
    getCurrentNumberCultureInfo,
    getCurrentDateTimeLocalizationInfo,
} from '@mews-commander/core/legacy-globalization';
import { getAppDateTimeNow } from '@mews-commander/utils';
import { getServiceDefaultProduct } from '@mews-commander/legacy-entities/src/ducks/products';
import { getNonDefaultServiceAbsolutelyPricedProducts } from '@mews-commander/legacy-entities/src/selectors/products';
import {
    getCurrentEnterpriseTaxEnvironmentCode,
    getCurrentEnterpriseDefaultCurrencyCode,
    getCurrentEnterprise,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getTaxationsByTaxEnvironmentCode } from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import { getServiceDefaultBusinessSegmentId } from '@mews-commander/legacy-entities/src/ducks/services';
import { ProductFieldNameResolverFn, ServiceFormValues } from '../types';
import * as productServiceOrderConstants from '../constants';

const { PRODUCT_SERVICE_FORM_NAME, ...fieldNames } = productServiceOrderConstants;

export const getDefaultProductServiceCurrency = createSelector(getServiceDefaultProduct, get('pricing.value.currency'));

export const getDefaultProductServiceAmount = createSelector(
    getServiceDefaultProduct,
    getCurrentEnterprise,
    getCurrentNumberCultureInfo,
    (serviceDefaultProduct, enterprise: Enterprise, cultureInfo) => {
        const value =
            enterprise.pricing === PricingMode.Net
                ? get('pricing.value.netValue')(serviceDefaultProduct)
                : get('pricing.value.grossValue')(serviceDefaultProduct);

        return NumberService.format(value, cultureInfo);
    }
);

const getDefaultProductServiceTaxValues = createSelector(getServiceDefaultProduct, (product) => {
    if (!product || product.pricing.discriminator === ProductPricing.Relative) {
        return [];
    }

    return product.pricing.value.taxValues;
});

export const getDefaultProductTaxRateValues = (
    state: ApplicationState,
    { serviceId }: WithServiceId
): Record<string, string> => {
    const defaultProductTaxValues = getDefaultProductServiceTaxValues(state, { serviceId }) || [];
    const taxEnvironmentCode: string = getCurrentEnterpriseTaxEnvironmentCode(state);
    const taxations: Taxation[] = getTaxationsByTaxEnvironmentCode(state, { taxEnvironmentCode });

    return defaultProductTaxValues.reduce(
        (accumulator, taxValue) => {
            const taxation = taxations.find((tax) => tax.taxRateCodes.includes(taxValue.taxRateCode));

            if (taxation) {
                accumulator[taxation.code] = taxValue.taxRateCode;
            }
            return accumulator;
        },
        {} as Record<string, string>
    );
};

export const getProductListInputInitialValues = createSelector(
    getNonDefaultServiceAbsolutelyPricedProducts,
    getCurrentEnterprise,
    getCurrentNumberCultureInfo,
    (products, enterprise: Enterprise, cultureInfo) =>
        pipe(
            products,
            array.filter(({ options }) => options.offerToEmployee),
            array.map((product) => {
                let unitCost = 0;
                const unitAmount = product.options.selectedByDefault ? 1 : 0;

                if (product.pricing.discriminator === ProductPricing.Absolute) {
                    unitCost =
                        enterprise.pricing === PricingMode.Net
                            ? product.pricing.value.netValue
                            : product.pricing.value.grossValue;
                }

                return [
                    product.id,
                    {
                        [fieldNames.UNIT_AMOUNT]: unitAmount,
                        [fieldNames.UNIT_COST]: NumberService.format(unitCost, cultureInfo),
                        [fieldNames.UNIT_CURRENCY]:
                            product.pricing.discriminator === ProductPricing.Absolute
                                ? product.pricing.value.currency
                                : '',
                    },
                ];
            }),
            Object.fromEntries
        )
);

export const getProductServiceProductUnitFieldValue = createSelector(
    getFormValues(PRODUCT_SERVICE_FORM_NAME),
    getStringProp('productId'),
    getProp<ProductFieldNameResolverFn>()('fieldNameResolver'),
    (productServiceFormValues, productId, fieldNameResolver) =>
        pipe(productServiceFormValues, get(fieldNameResolver(productId)))
);

const getLocalizedTodayDateTime = createSelector(
    getCurrentDateTimeLocalizationInfo,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getProp<AppClockService>()('appClockService'),
    (localizationInfo, timezone: string, appClockService) => {
        const nowDateTime = getAppDateTimeNow({ appClockService, timezone });
        const todayDate = DateTimeService.toDate(nowDateTime);
        const todayTime = DateTimeService.toTime(nowDateTime);

        return {
            date: DateService.toString(todayDate, localizationInfo),
            time: TimeService.toShortString(todayTime, localizationInfo),
        };
    }
);

export const getServiceOrderFormInitialValues = (
    state: ApplicationState,
    {
        serviceId,
        customerId,
        appClockService,
    }: { serviceId: string | undefined; customerId: string; appClockService: AppClockService }
): ServiceFormValues => {
    const defaultCurrency = getCurrentEnterpriseDefaultCurrencyCode(state);
    return {
        [fieldNames.SERVICE_ID]: serviceId,
        [fieldNames.CUSTOMER_ID]: customerId,
        [fieldNames.CURRENCY]: defaultCurrency,
        ...getLocalizedTodayDateTime(state, { appClockService }),
        ...(serviceId
            ? {
                  [fieldNames.CURRENCY]: getDefaultProductServiceCurrency(state, { serviceId }),
                  [fieldNames.AMOUNT]: getDefaultProductServiceAmount(state, { serviceId }),
                  [fieldNames.UNITS]: getProductListInputInitialValues(state, { serviceId }),
                  [fieldNames.SEGMENT]: getServiceDefaultBusinessSegmentId(state, { id: serviceId }),
                  [fieldNames.TAX_RATE]: getDefaultProductTaxRateValues(state, { serviceId }),
              }
            : {}),
    };
};
